import { toast } from 'react-toastify';

import { getNotificationContent } from 'components';
import { SCAN_NAMES } from 'consts';
import { store } from 'store';
import { getScanUrl } from 'utils/helpers';
import { handleWeb3Error } from 'utils/helpers/handleError';
import { Address } from 'viem';
import { usePublicClient, useSendTransaction } from 'wagmi';

import { useTransactionExpired } from '../useTransactionExpired';

import { AddTokenButton } from './AddTokenButton';
import { parseLogs, stopSwapByGasError } from './helpers';

type SwapParams = {
  to: Address;
  data: `0x${string}`;
  value: string;
  gas: number;
};

export const useSwap = () => {
  const { chainId, account } = store.web3Store;
  const { tokenOutAddress, setValue } = store.swapStore;

  const { sendTransactionAsync } = useSendTransaction();
  const publicClient = usePublicClient({ chainId });
  const { setExpiredTimer, clearExpiredTimer } = useTransactionExpired();

  const swap = async (params: SwapParams) => {
    if (!publicClient || !account) return;

    const estimationParams = {
      account,
      to: params.to,
      data: params.data,
      value: BigInt(params.value),
      chainId,
    };

    let gas: bigint;

    try {
      gas = await publicClient.estimateGas(estimationParams);
    } catch (err: any) {
      console.log('swap gas error', { ...err });
      const shouldStop = stopSwapByGasError(err);
      if (shouldStop) return;

      gas = BigInt(params.gas);
    }
    console.log('swap gas', gas);

    const transactionParams = {
      ...estimationParams,
      gas: BigInt(Math.ceil(Number(gas) * 1.2)), // +20%
    };
    console.log('swap transactionParams', transactionParams);

    try {
      setExpiredTimer();
      const hash = await sendTransactionAsync(transactionParams);
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      console.log('swap receipt', receipt);
      const scanLink = getScanUrl(`/tx/${receipt.transactionHash}`, chainId);

      if (receipt.status === 'success') {
        const successMessage = parseLogs(receipt, params.value, chainId);
        toast.success(
          getNotificationContent({
            title: 'Transaction Succeeded!',
            text: successMessage,
            href: scanLink,
            scanName: SCAN_NAMES[chainId],
            footerContent: () => (
              <AddTokenButton tokenAddress={tokenOutAddress} chainId={chainId} />
            ),
          })
        );
        setValue('');
      }

      if (receipt.status === 'reverted') {
        toast.error(
          getNotificationContent({
            isError: true,
            title: 'Failed to swap',
            href: scanLink,
            scanName: SCAN_NAMES[chainId],
          })
        );
      }
    } catch (err: any) {
      handleWeb3Error({ err, source: 'swap' });
    }

    clearExpiredTimer();
  };

  return swap;
};
